import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent, EVENTS } from '../../lib/analyticsV2';
import { getUTMs } from '../../hooks/useUTMs';

const steps = [
  { id: 'objective', title: 'Qual é seu principal objetivo?' },
  { id: 'horizon', title: 'Em que horizonte você pensa?' },
  { id: 'priority', title: 'O que é mais importante agora?' },
  { id: 'contact', title: 'Como podemos enviar a análise?' }
];

const ProgressiveDiagnosticV2 = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const { register, handleSubmit, formState: { errors, isValid }, trigger, getValues, watch } = useForm({ mode: 'onChange' });

  const nextStep = async () => {
    const fieldsToValidate = currentStep === 0 ? ['objective'] 
                           : currentStep === 1 ? ['horizon'] 
                           : currentStep === 2 ? ['priority'] 
                           : [];
    
    const isStepValid = await trigger(fieldsToValidate);
    
    if (isStepValid) {
      if (currentStep === 0) trackEvent(EVENTS.DIAGNOSTIC_START, { diagnostic_version: 'v2_progressive' });
      trackEvent(EVENTS.DIAGNOSTIC_STEP_COMPLETE, { step_number: currentStep + 1, diagnostic_version: 'v2_progressive' });
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const onSubmit = async (data) => {
    if (data.honeypot) return;

    setStatus('loading');
    
    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      objective: data.objective,
      horizon: data.horizon,
      priority: data.priority,
      source: "landing_patrimonio_v2",
      utm: getUTMs(),
      consents: {
        privacy: data.privacyConsent,
        marketing: data.marketingConsent || false,
        privacyPolicyVersion: "v1.0"
      },
      honeypot: data.honeypot
    };

    try {
      const response = await fetch('/api/leads/v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok || response.status === 202) {
        setStatus('success');
        trackEvent(EVENTS.LEAD_FORM_SUBMIT, {
          form_id: 'progressive_diagnostic_v2',
          objective_category: data.objective,
          horizon_category: data.horizon
        });
      } else {
        throw new Error('Falha no envio');
      }
    } catch (error) {
      console.error(error);
      setStatus('error');
      trackEvent(EVENTS.LEAD_FORM_ERROR, {
        form_id: 'progressive_diagnostic_v2',
        error_code: 'network_error'
      });
    }
  };

  const currentObjective = watch('objective');
  const currentHorizon = watch('horizon');
  const currentPriority = watch('priority');

  return (
    <section id="diagnostico" className="py-16 md:py-24 bg-background">
      <div className="container px-4 md:px-6 mx-auto max-w-2xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl mb-4">
            Diagnóstico Patrimonial
          </h2>
          <p className="text-muted-foreground">
            Responda a estas breves perguntas para entendermos o seu caso e indicarmos o próximo passo.
          </p>
        </div>

        {status === 'success' ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-8 text-center border rounded-lg bg-green-500/10 text-green-700 dark:text-green-400">
            <h3 className="text-xl font-bold mb-2">Análise Solicitada com Sucesso</h3>
            <p>A equipe fará uma triagem inicial. Enviaremos os próximos passos pelo WhatsApp ou e-mail informado.</p>
          </motion.div>
        ) : (
          <div className="border rounded-xl p-6 md:p-8 bg-card shadow-sm relative overflow-hidden">
            {/* Progress Bar */}
            <div className="absolute top-0 left-0 h-1 bg-muted w-full">
              <motion.div 
                className="h-full bg-primary"
                initial={{ width: '0%' }}
                animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>

            <div className="mb-6 mt-2 flex justify-between items-center text-sm font-medium text-muted-foreground">
              <span>Etapa {currentStep + 1} de {steps.length}</span>
              <span>{steps[currentStep].title}</span>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="hidden" aria-hidden="true">
                <input type="text" {...register("honeypot")} tabIndex="-1" autoComplete="off" />
              </div>

              <div className="min-h-[250px] relative">
                <AnimatePresence mode="wait">
                  {currentStep === 0 && (
                    <motion.div key="step0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                      <div className="grid gap-3">
                        {[
                          { value: 'first_property', label: 'Primeiro imóvel' },
                          { value: 'income_property', label: 'Imóvel para renda' },
                          { value: 'diversification', label: 'Diversificação' },
                          { value: 'business', label: 'Empresa' },
                          { value: 'other', label: 'Outro' }
                        ].map((opt) => (
                          <label key={opt.value} className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors hover:bg-accent ${currentObjective === opt.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}>
                            <input type="radio" value={opt.value} {...register("objective", { required: "Selecione uma opção" })} className="sr-only" />
                            <span className="font-medium">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      {errors.objective && <p className="text-red-500 text-sm mt-2">{errors.objective.message}</p>}
                    </motion.div>
                  )}

                  {currentStep === 1 && (
                    <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                      <div className="grid gap-3">
                        {[
                          { value: 'up_to_2y', label: 'Até 2 anos' },
                          { value: '2_to_5y', label: '2 a 5 anos' },
                          { value: 'over_5y', label: 'Mais de 5 anos' },
                          { value: 'unknown', label: 'Ainda não sei' }
                        ].map((opt) => (
                          <label key={opt.value} className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors hover:bg-accent ${currentHorizon === opt.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}>
                            <input type="radio" value={opt.value} {...register("horizon", { required: "Selecione uma opção" })} className="sr-only" />
                            <span className="font-medium">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      {errors.horizon && <p className="text-red-500 text-sm mt-2">{errors.horizon.message}</p>}
                    </motion.div>
                  )}

                  {currentStep === 2 && (
                    <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                      <div className="grid gap-3">
                        {[
                          { value: 'alternatives', label: 'Entender alternativas' },
                          { value: 'costs', label: 'Comparar custos' },
                          { value: 'timing', label: 'Avaliar prazo' },
                          { value: 'cashflow', label: 'Organizar fluxo de caixa' },
                          { value: 'other', label: 'Outro' }
                        ].map((opt) => (
                          <label key={opt.value} className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors hover:bg-accent ${currentPriority === opt.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}>
                            <input type="radio" value={opt.value} {...register("priority", { required: "Selecione uma opção" })} className="sr-only" />
                            <span className="font-medium">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      {errors.priority && <p className="text-red-500 text-sm mt-2">{errors.priority.message}</p>}
                    </motion.div>
                  )}

                  {currentStep === 3 && (
                    <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Nome completo</label>
                        <input 
                          type="text" 
                          {...register("name", { required: "Nome é obrigatório" })} 
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                        {errors.name && <span className="text-red-500 text-xs">{errors.name.message}</span>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">E-mail</label>
                        <input 
                          type="email" 
                          {...register("email", { required: "E-mail é obrigatório" })} 
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                        {errors.email && <span className="text-red-500 text-xs">{errors.email.message}</span>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">WhatsApp</label>
                        <input 
                          type="tel" 
                          {...register("phone", { required: "WhatsApp é obrigatório" })} 
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                        {errors.phone && <span className="text-red-500 text-xs">{errors.phone.message}</span>}
                      </div>
                      
                      <div className="pt-4 border-t mt-6 space-y-3">
                        <label className="flex items-start gap-2">
                          <input type="checkbox" {...register("privacyConsent", { required: "Aceite obrigatório" })} className="mt-1" />
                          <span className="text-xs text-muted-foreground">Li a Política de Privacidade e concordo com o tratamento dos dados.*</span>
                        </label>
                        {errors.privacyConsent && <span className="text-red-500 text-xs block">{errors.privacyConsent.message}</span>}
                        <label className="flex items-start gap-2">
                          <input type="checkbox" {...register("marketingConsent")} className="mt-1" />
                          <span className="text-xs text-muted-foreground">Aceito receber materiais informativos.</span>
                        </label>
                      </div>

                      {status === 'error' && (
                        <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm mt-4">
                          Ocorreu um erro. Tente novamente mais tarde.
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="flex justify-between mt-8 pt-4 border-t border-border/50">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 0 || status === 'loading'}
                  className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  Voltar
                </button>
                
                {currentStep < steps.length - 1 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-8 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
                  >
                    Continuar
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-8 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    {status === 'loading' ? 'Enviando...' : 'Solicitar análise'}
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProgressiveDiagnosticV2;
